import { Component } from '@theme/component';
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent
} from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(
      '.shopify-section, dialog'
    );

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event.
   *
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    /*
     * CAPRICCIO:
     * Evita reemplazar esta galería al cambiar
     * color, talla o modelo.
     *
     * Esto mantiene la imagen seleccionada,
     * evita el parpadeo y conserva el orden
     * de las miniaturas.
     */
    if (
      this.hasAttribute(
        'data-capriccio-static-gallery'
      )
    ) {
      return;
    }

    const source = event.detail.data.html;

    if (!source) return;

    const newMediaGallery =
      source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   *
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (
    event
  ) => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      { animate: false }
    );
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(
      index,
      event
    );
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    MediaGallery
  );
}